import { useCallback, useEffect, useState } from 'react';
import { ApiError, api } from '../lib/api';
import type { AgentProfile, Attachment, Project } from '../lib/api';
import { AttachmentsField } from './AttachmentsField';
import { PopChoice } from './PopChoice';
import { Button } from '../components/Button';
import { ChoiceCards } from '../components/ChoiceCards';
import { CodeBlock } from '../components/CodeBlock';
import { Field, TextArea, TextField } from '../components/Field';
import { Icon } from '../components/Icon';
import { Modal } from '../components/Modal';
import { Select } from '../components/Select';
import { useStore } from '../store/StoreProvider';

type Mode = 'plan' | 'together' | 'chat';

export function ModalHost({ onNavigate }: { onNavigate: (path: string) => void }) {
  const { state, closeModal, toast, reload } = useStore();
  const m = state.modal;
  const [text, setText] = useState('');
  const [project, setProject] = useState('');
  const [mode, setMode] = useState<Mode>('plan');
  const [profiles, setProfiles] = useState<AgentProfile[]>([]);
  const [talker, setTalker] = useState('');
  const [busy, setBusy] = useState(false);
  const [pw, setPw] = useState({ current: '', next: '', repeat: '' });
  const [choices, setChoices] = useState<Project[]>([]);
  const [defaultMb, setDefaultMb] = useState(25);
  const [files, setFiles] = useState<{ ready: Attachment[]; uploading: boolean }>({
    ready: [],
    uploading: false,
  });
  const onFiles = useCallback(
    (ready: Attachment[], uploading: boolean) => setFiles({ ready, uploading }),
    [],
  );
  const asking = m?.kind === 'new-request';

  // i progetti per «Nuova richiesta», con il predefinito già scelto; i pop per la Conversazione,
  // con il PM di partenza; il modo può arrivare da chi apre il modale («Nuova conversazione»)
  useEffect(() => {
    if (!asking) return;
    const wanted = m?.payload?.mode;
    if (wanted === 'chat' || wanted === 'plan' || wanted === 'together') setMode(wanted);
    api.profiles().then(
      (p) => {
        setProfiles(p);
        setTalker((t) => t || (p.find((x) => x.coordinator)?.id ?? p[0]?.id ?? ''));
      },
      () => setProfiles([]),
    );
    api.projects().then(
      (p) => {
        setChoices(p.projects);
        setDefaultMb(p.defaults.docsMaxMb);
        setProject(p.projects.find((x) => x.default)?.id ?? '');
      },
      () => setChoices([]),
    );
  }, [asking, m?.payload?.mode]);

  if (!m) return null;

  const close = () => {
    setText('');
    setMode('plan');
    setBusy(false);
    closeModal();
  };

  if (m.kind === 'new-request') {
    const submit = async () => {
      if (!text.trim()) return;
      setBusy(true);
      try {
        const r = await api.createSession({
          text: text.trim(),
          channel: 'text',
          project: project.trim() || undefined,
          mode,
          profile: mode === 'chat' ? talker || undefined : undefined,
          attachments: files.ready.map((a) => a.id),
        });
        toast({ kind: 'success', title: 'Richiesta inviata', body: text.trim() });
        close();
        reload();
        onNavigate((mode === 'chat' ? '/conversazioni/' : '/s/') + (r.queuedTo ?? r.id));
      } catch (e) {
        // il server dice cosa manca: si mostra così com'è
        toast({
          kind: 'danger',
          title: 'La richiesta non è partita',
          body: e instanceof ApiError && e.detail ? e.detail : 'Riprova fra poco.',
        });
        setBusy(false);
      }
    };
    return (
      <Modal
        title="Nuova richiesta"
        onClose={close}
        footer={
          <>
            <Button variant="ghost" onClick={close}>
              Annulla
            </Button>
            <Button
              variant="primary"
              loading={busy}
              onClick={submit}
              disabled={!text.trim() || files.uploading}
            >
              {mode === 'plan' ? 'Invia' : 'Inizia'}
            </Button>
          </>
        }
      >
        <ChoiceCards
          label="Come vuoi partire"
          value={mode}
          onChange={setMode}
          items={[
            {
              value: 'chat',
              title: 'Conversazione',
              hint: 'Parli con un pop: risponde e usa i suoi strumenti. Nessun piano, nessun team.',
              icon: 'message-circle-question',
            },
            {
              value: 'plan',
              title: 'Ho già un piano',
              hint: 'Lo consegni al PM: decide la squadra e fa lavorare i team.',
              icon: 'send',
            },
            {
              value: 'together',
              title: 'Pianifichiamo insieme',
              hint: 'Ne parli con il Team Leader e il PM. Poi il PM scrive il Piano di PopTown: lo approvi e parte.',
              icon: 'messages-square',
            },
          ]}
        />
        {mode === 'chat' && <PopChoice profiles={profiles} value={talker} onChange={setTalker} />}
        <Field label={mode === 'plan' ? 'Cosa serve?' : 'Di cosa vuoi parlare?'}>
          <TextArea value={text} onChange={setText} rows={4} autoFocus />
        </Field>
        <AttachmentsField
          project={project}
          maxMb={choices.find((p) => p.id === project)?.docsMaxMb ?? defaultMb}
          onChange={onFiles}
        />
        <Field label="Progetto" hint={projectHint(choices, project)}>
          <Select
            label="Progetto"
            value={project}
            onChange={setProject}
            options={[
              ...choices.map((p) => ({ value: p.id, label: projectLabel(p) })),
              { value: '', label: 'Nessun progetto' },
            ]}
            width="100%"
          />
        </Field>
      </Modal>
    );
  }

  if (m.kind === 'json') {
    return (
      <Modal
        title={m.title}
        onClose={close}
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => void navigator.clipboard.writeText(String(m.payload?.json ?? ''))}
            >
              Copia JSON
            </Button>
            <Button variant="primary" onClick={close}>
              Chiudi
            </Button>
          </>
        }
      >
        <div className="t-mono-xs faint">{String(m.payload?.id ?? '')}</div>
        <CodeBlock code={String(m.payload?.json ?? '{}')} />
      </Modal>
    );
  }

  if (m.kind === 'confirm') {
    const run = async () => {
      setBusy(true);
      const fn = m.payload?.run as undefined | (() => Promise<void> | void);
      try {
        await fn?.();
      } catch (e) {
        // un'azione rifiutata (sessione già chiusa, Temporal giù) si dice: il modale non
        // sparisce in silenzio lasciando credere che sia andata
        toast({
          kind: 'danger',
          title: m.title,
          body: e instanceof ApiError && e.detail ? e.detail : 'Non è riuscito: riprova.',
        });
      } finally {
        setBusy(false);
        close();
      }
    };
    return (
      <Modal
        title={m.title}
        width={480}
        onClose={close}
        footer={
          <>
            <Button variant="ghost" onClick={close}>
              Annulla
            </Button>
            <Button
              variant="primary"
              loading={busy}
              onClick={run}
              style={{ background: 'var(--danger)', color: 'var(--danger-ink)' }}
            >
              {m.okLabel ?? 'Conferma'}
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
          <span
            style={{
              width: 48,
              height: 48,
              flex: 'none',
              borderRadius: '50%',
              background: 'var(--danger-soft)',
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <Icon name="alert-triangle" size={24} color="var(--danger)" />
          </span>
          <span className="t-body dim">{m.body}</span>
        </div>
      </Modal>
    );
  }

  if (m.kind === 'change-password') {
    const invalid = pw.next.length > 0 && pw.next !== pw.repeat;
    const run = async () => {
      setBusy(true);
      try {
        await api.changePassword({ current: pw.current, next: pw.next });
      } catch (e) {
        toast({
          kind: 'danger',
          title: 'Password non cambiata',
          body: e instanceof ApiError && e.detail ? e.detail : 'Non è riuscito: riprova.',
        });
        return;
      } finally {
        setBusy(false);
      }
      toast({ kind: 'success', title: 'Password aggiornata' });
      setPw({ current: '', next: '', repeat: '' });
      close();
    };
    return (
      <Modal
        title="Cambia password"
        width={480}
        onClose={close}
        footer={
          <>
            <Button variant="ghost" onClick={close}>
              Annulla
            </Button>
            <Button
              variant="primary"
              loading={busy}
              disabled={!pw.current || !pw.next || invalid}
              onClick={run}
            >
              Salva
            </Button>
          </>
        }
      >
        <Field label="Password attuale">
          <TextField
            type="password"
            value={pw.current}
            onChange={(v) => setPw({ ...pw, current: v })}
          />
        </Field>
        <Field label="Nuova password">
          <TextField type="password" value={pw.next} onChange={(v) => setPw({ ...pw, next: v })} />
        </Field>
        <Field
          label="Ripeti la nuova password"
          error={invalid ? 'Le due password non coincidono.' : undefined}
        >
          <TextField
            type="password"
            value={pw.repeat}
            onChange={(v) => setPw({ ...pw, repeat: v })}
            invalid={invalid}
          />
        </Field>
      </Modal>
    );
  }

  return null;
}

function projectLabel(p: Project): string {
  return `${p.name} · ${p.git ? p.git.repo : 'senza git'}`;
}

// sotto la scelta: dove lavorerà la sessione
function projectHint(projects: Project[], id: string): string {
  const p = projects.find((x) => x.id === id);
  if (!p) return 'Senza progetto la sessione lavora nel suo spazio locale.';
  const push = {
    task: 'push dopo ogni lavoro',
    mission: 'push a fine missione',
    manual: 'push a mano',
  };
  return `Lavora su ${p.workBranch}${p.git ? ', ' + push[p.push] : ', solo in locale'}.`;
}
